import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatUGX } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import PropertyMap from "@/components/PropertyMap";
import { MapPin, Maximize, FileBadge, Phone, MessageSquare, ArrowLeft, CheckCircle2, Play, Wallet, Share2 } from "lucide-react";
import { toast } from "sonner";
import { monthlyPayment, formatUGXShort, DEFAULT_DOWN_PCT, DEFAULT_TERM_YEARS, DEFAULT_RATE_APR } from "@/lib/mortgage";
import StoryCard from "@/components/StoryCard";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [down, setDown] = useState(DEFAULT_DOWN_PCT);
  const [term, setTerm] = useState(DEFAULT_TERM_YEARS);
  const [rate, setRate] = useState(DEFAULT_RATE_APR);

  useEffect(() => {
    api.get(`/properties/${id}`)
      .then((r) => setP(r.data))
      .catch(() => toast.error("Property not found"));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { ...form, interest: "Property Inquiry", property_id: id });
      toast.success("Inquiry sent! We'll reach out shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  if (!p) {
    return <main className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center text-muted-foreground" data-testid="property-loading">Loading…</main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="property-detail">
      <Link to="/listings" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-listings">
        <ArrowLeft className="w-4 h-4" /> Back to listings
      </Link>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-muted border border-border" data-testid="detail-hero">
            {p.video_url && showVideo ? (
              <video
                src={p.video_url}
                controls autoPlay playsInline
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="property-video"
              />
            ) : (
              <>
                <img src={p.images?.[activeImg] || p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
                {p.video_url && (
                  <button
                    onClick={() => setShowVideo(true)}
                    className="absolute inset-0 grid place-items-center group"
                    aria-label="Play video walkthrough"
                    data-testid="play-reel-btn"
                  >
                    <div className="absolute inset-0 bg-black/25 group-hover:bg-black/35 transition-colors" />
                    <div className="relative flex items-center gap-3 bg-background/95 rounded-full pl-3 pr-6 py-2 shadow-2xl">
                      <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center">
                        <Play className="w-4 h-4 fill-current translate-x-0.5" />
                      </span>
                      <span className="text-sm font-semibold">Watch 30-sec reel</span>
                    </div>
                  </button>
                )}
              </>
            )}
          </div>
          {p.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {p.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-[4/3] rounded-xl overflow-hidden border ${i === activeImg ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-10">
            <div className="eyebrow mb-3">{p.property_type}</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter">{p.title}</h1>
            <div className="mt-3 flex items-center gap-2 text-muted-foreground">
              <MapPin className="w-4 h-4 text-primary" /> {p.location} · {p.district}, {p.region} Uganda
            </div>

            <div className="grid grid-cols-3 gap-4 mt-8">
              <Spec icon={Maximize} label="Size" value={`${p.size} ${p.size_unit}`} />
              <Spec icon={FileBadge} label="Title" value={p.title_status} />
              <Spec icon={MapPin} label="Region" value={p.region} />
            </div>

            <div className="mt-10">
              <h2 className="font-display text-2xl mb-3">About this property</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>

            {p.features?.length > 0 && (
              <div className="mt-10">
                <h2 className="font-display text-2xl mb-4">Features</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {p.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 bg-muted/50 rounded-xl px-4 py-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {p.lat != null && p.lng != null && (
              <div className="mt-10" data-testid="property-map-section">
                <h2 className="font-display text-2xl mb-4">Location</h2>
                <PropertyMap lat={p.lat} lng={p.lng} title={p.title} height={380} />
                <p className="text-xs text-muted-foreground mt-3 font-mono">Approximate. Contact us for exact GPS coordinates on inspection.</p>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-32 space-y-4">
          <div className="bg-card border border-border rounded-3xl p-7">
            <div className="eyebrow mb-2">Asking Price</div>
            <div className="font-display text-4xl font-bold text-primary">{formatUGX(p.price_ugx)}</div>
            <div className="text-xs text-muted-foreground mt-1 font-mono">Negotiable · inspection welcome</div>

            <div className="mt-6 flex flex-col gap-3">
              <Button asChild className="rounded-full bg-primary hover:bg-primary/90 h-12" data-testid="whatsapp-btn">
                <a href={`https://wa.me/16145719333?text=Hi, I'm interested in ${encodeURIComponent(p.title)}`} target="_blank" rel="noreferrer">
                  <MessageSquare className="w-4 h-4" /> WhatsApp Agent
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 border-border" data-testid="call-btn">
                <a href="tel:+256700259675"><Phone className="w-4 h-4" /> Call +256 700 259 675</a>
              </Button>
              <StoryCard property={p} trigger={
                <Button variant="outline" className="rounded-full h-12 border-border" data-testid="story-card-open-btn">
                  <Share2 className="w-4 h-4" /> Share as Story Card
                </Button>
              } />
            </div>

            <form onSubmit={submit} className="mt-8 space-y-3 border-t border-border pt-6" data-testid="inquiry-form">
              <h3 className="font-display text-lg font-semibold">Request inspection</h3>
              <div>
                <Label htmlFor="name" className="text-xs">Full name</Label>
                <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="inquiry-name" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs">Phone / WhatsApp</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" data-testid="inquiry-phone" />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs">Email (optional)</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl" data-testid="inquiry-email" />
              </div>
              <div>
                <Label htmlFor="message" className="text-xs">Message</Label>
                <Textarea id="message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" placeholder={`I'd like to inspect ${p.title}`} data-testid="inquiry-message" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full rounded-full bg-primary hover:bg-primary/90 h-11" data-testid="inquiry-submit">
                {submitting ? "Sending…" : "Send Inquiry"}
              </Button>
            </form>
          </div>

          <LoanEstimator
            price={p.price_ugx}
            down={down} setDown={setDown}
            term={term} setTerm={setTerm}
            rate={rate} setRate={setRate}
          />
          </div>
        </aside>
      </div>
    </main>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="bg-muted/50 rounded-2xl p-4">
      <Icon className="w-4 h-4 text-primary" />
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground mt-2">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function LoanEstimator({ price, down, setDown, term, setTerm, rate, setRate }) {
  const monthly = monthlyPayment({ price, downPct: down, termYears: term, rateApr: rate });
  const totalInterest = monthly * term * 12 - price * (1 - down / 100);
  const [banks, setBanks] = useState([]);
  useEffect(() => {
    api.get("/bank-rates").then((r) => setBanks(r.data.rates || [])).catch(() => {});
  }, []);
  return (
    <div className="bg-card border border-border rounded-3xl p-7" data-testid="loan-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="w-4 h-4 text-primary" />
        <div className="eyebrow">Monthly estimate</div>
      </div>
      <div className="font-display text-4xl font-bold" data-testid="loan-monthly">{formatUGXShort(monthly)}<span className="text-lg text-muted-foreground font-medium">/mo</span></div>
      <div className="text-xs text-muted-foreground mt-1 font-mono">Assuming {down}% down · {term}y · {rate}% APR</div>

      <div className="mt-6 space-y-5">
        <Row label="Down payment" value={`${down}%`}>
          <Slider value={[down]} onValueChange={(v) => setDown(v[0])} min={0} max={80} step={5} data-testid="slider-down" />
        </Row>
        <Row label="Loan term" value={`${term} years`}>
          <Slider value={[term]} onValueChange={(v) => setTerm(v[0])} min={5} max={25} step={1} data-testid="slider-term" />
        </Row>
        <Row label="Interest rate" value={`${rate.toFixed(1)}%`}>
          <Slider value={[rate]} onValueChange={(v) => setRate(v[0])} min={10} max={25} step={0.5} data-testid="slider-rate" />
        </Row>
      </div>

      <div className="mt-6 pt-5 border-t border-border grid grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Loan amount</div>
          <div className="font-display text-lg font-bold mt-1">{formatUGXShort(price * (1 - down / 100))}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground">Total interest</div>
          <div className="font-display text-lg font-bold mt-1">{formatUGXShort(totalInterest)}</div>
        </div>
      </div>

      {banks.length > 0 && (
        <div className="mt-6 pt-5 border-t border-border" data-testid="bank-rates-strip">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-muted-foreground mb-3">Compare with partner banks</div>
          <div className="space-y-1.5 max-h-56 overflow-auto pr-1">
            {banks.map((b) => {
              const m = monthlyPayment({ price, downPct: down, termYears: Math.min(term, b.term_max_years || term), rateApr: b.rate_apr });
              return (
                <button
                  key={b.bank}
                  onClick={() => setRate(b.rate_apr)}
                  className="w-full flex items-center justify-between text-xs bg-muted/40 hover:bg-muted rounded-lg px-3 py-2 transition-colors"
                  data-testid={`bank-rate-${b.bank.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <span className="font-semibold">{b.bank}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-muted-foreground">{b.rate_apr}%</span>
                    <span className="font-display font-bold text-primary">{formatUGXShort(m)}<span className="text-muted-foreground font-medium">/mo</span></span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground mt-5 leading-relaxed">Estimate only. Tap any bank to load its rate. Actual terms depend on your credit and property assessment.</p>
    </div>
  );
}

function Row({ label, value, children }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-2">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono font-semibold">{value}</span>
      </div>
      {children}
    </div>
  );
}
